import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Save, ArrowLeft, ExternalLink } from 'lucide-react';
import { getAdminEvents, createEvent, updateEvent } from '../../services/api';
import ImageUploader from './ImageUploader';
import { Field, Area, Toggle, StringList, Repeater } from './EventFormFields';
import { FALLBACK_EVENTS, eventSlug } from '../../utils/eventData';

const TABS = [
  ['genel', 'Genel'],
  ['hero', 'Hero'],
  ['rakamlar', 'Rakam Kartları'],
  ['sohbetler', 'Akşam Sohbetleri'],
  ['program', 'Gün Gün Program'],
  ['dahil', 'Dahil / Hariç'],
  ['konuklar', 'Konuklar'],
  ['partnerler', 'Partnerler'],
  ['seo', 'SEO & Ana Sayfa'],
];

const toLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const emptyEvent = () => ({
  name: '', slug: '', active: true,
  startISO: '', endISO: '', dateLabel: '', dayLabel: '', durationLabel: '',
  route: '', totalKm: 0, meetingPoint: '', price: 0, currency: '₺', priceLabel: '',
  capacity: 0, locationCity: '', locationRegion: '', nights: 0, singleRoomSupplement: 0,
  heroImage: '', heroImageFallback: '', heroImageAlt: '',
  hero: { eyebrow: '', title: '', subtitle: '', primaryCta: 'Yerinizi Ayırtın', secondaryCta: 'Programı İnceleyin' },
  highlights: [], talkGroups: [], itinerary: [], included: [], excluded: [],
  talks: { eyebrow: 'Akşam Sohbetleri', title: '', subtitle: '', topics: [] },
  guests: [], partners: [],
  seo: { title: '', description: '' },
  homePromo: { badge: 'Özel Etkinlik', title: '', subtitle: '', cta: 'Detayları İnceleyin' },
});

const AdminEventForm = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(eventId);
  const [tab, setTab] = useState('genel');
  const [form, setForm] = useState(emptyEvent);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');

  useEffect(() => {
    if (!isEdit) return;
    getAdminEvents()
      .then((r) => {
        const found = (r.data || []).find((e) => e.id === eventId);
        if (!found) {
          setError('Etkinlik bulunamadı');
          return;
        }
        const merged = { ...emptyEvent(), ...found };
        // Eski "Grup 1 / Grup 2" yapısından tek listeye taşıma
        if (!merged.talks?.topics?.length) {
          const tpl = FALLBACK_EVENTS.find((e) => eventSlug(e) === eventSlug(merged))?.talks || {};
          const flat = (merged.talkGroups || []).flatMap((g) => g.topics || []);
          merged.talks = {
            eyebrow: merged.talks?.eyebrow || tpl.eyebrow || 'Akşam Sohbetleri',
            title: merged.talks?.title || tpl.title || '',
            subtitle: merged.talks?.subtitle || tpl.subtitle || '',
            topics: tpl.topics?.length ? tpl.topics : flat,
          };
          if (merged.talks.topics.length) {
            setOkMsg('Akşam Sohbetleri bölümü yeni tek liste yapısına taşındı. Kontrol edip Kaydet\'e basın.');
          }
        }
        setForm(merged);
      })
      .catch(() => setError('Etkinlik yüklenemedi'))
      .finally(() => setLoading(false));
  }, [eventId, isEdit]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const setNested = (group, key) => (value) =>
    setForm((f) => ({ ...f, [group]: { ...(f[group] || {}), [key]: value } }));

  const publicUrl = useMemo(() => `/${eventSlug(form)}`, [form]);

  const importTemplate = () => {
    const tpl = FALLBACK_EVENTS[0];
    setForm({ ...emptyEvent(), ...tpl, slug: eventSlug(tpl) });
    setOkMsg('Likya 49W şablonu forma yüklendi. Kaydet ile oluşturabilirsiniz.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setOkMsg('');
    if (!form.name || !eventSlug(form)) {
      setError('Etkinlik adı ve slug zorunludur.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, slug: eventSlug(form) };
      if (isEdit) {
        await updateEvent(eventId, payload);
        setOkMsg('Değişiklikler kaydedildi.');
      } else {
        const r = await createEvent(payload);
        navigate(`/admin/etkinlikler/${r.data.id}/duzenle`, { replace: true });
        setOkMsg('Etkinlik oluşturuldu.');
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-gray-500">Yükleniyor...</p>;

  return (
    <form onSubmit={handleSubmit} className="max-w-4xl" data-testid="admin-event-form">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <Link to="/admin/etkinlikler" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-[#3D4A26]">
          <ArrowLeft size={15} /> Etkinlikler
        </Link>
        <div className="flex items-center gap-3">
          {!isEdit && (
            <button
              type="button"
              onClick={importTemplate}
              data-testid="admin-event-import-template"
              className="text-sm font-semibold text-[#3D4A26] hover:underline"
            >
              Likya 49W şablonunu yükle
            </button>
          )}
          {isEdit && (
            <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-[#3D4A26]">
              Sayfayı gör <ExternalLink size={14} />
            </a>
          )}
          <button
            type="submit"
            disabled={saving}
            data-testid="admin-event-save"
            className="inline-flex items-center gap-2 bg-[#3D4A26] text-white font-bold text-sm px-5 py-2.5 rounded-lg hover:bg-[#2d3a1a] disabled:opacity-60"
          >
            <Save size={15} /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>

      {error && <p className="mb-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="admin-event-error">{error}</p>}
      {okMsg && <p className="mb-4 text-sm text-[#3D4A26] bg-[#f0f4d8] border border-[#C8D843]/40 rounded-lg px-3 py-2" data-testid="admin-event-success">{okMsg}</p>}

      <div className="flex flex-wrap gap-1.5 pb-3 mb-5 border-b border-gray-200">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            data-testid={`admin-event-tab-${key}`}
            className={`px-3.5 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${
              tab === key ? 'bg-[#3D4A26] text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-5">
        {tab === 'genel' && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Etkinlik adı" value={form.name} onChange={set('name')} testId="admin-event-name" />
              <Field label="Slug (URL)" value={form.slug} onChange={set('slug')} hint={`Sayfa adresi: camption.com${publicUrl}`} testId="admin-event-slug" />
              <Field label="Başlangıç tarihi ve saati" type="datetime-local" value={toLocal(form.startISO)} onChange={(v) => set('startISO')(v ? new Date(v).toISOString() : '')} testId="admin-event-start" />
              <Field label="Bitiş tarihi ve saati" type="datetime-local" value={toLocal(form.endISO)} onChange={(v) => set('endISO')(v ? new Date(v).toISOString() : '')} testId="admin-event-end" />
              <Field label="Tarih etiketi" value={form.dateLabel} onChange={set('dateLabel')} hint="Örn: 7-11 Kasım 2026" />
              <Field label="Gün etiketi" value={form.dayLabel} onChange={set('dayLabel')} hint="Örn: Cumartesi - Çarşamba" />
              <Field label="Süre" value={form.durationLabel} onChange={set('durationLabel')} hint="Örn: 4 gece 5 gün" />
              <Field label="Buluşma / dönüş" value={form.meetingPoint} onChange={set('meetingPoint')} />
              <Field label="Toplam yürüyüş (km)" type="number" value={form.totalKm} onChange={set('totalKm')} />
              <Field label="Kontenjan (kişi)" type="number" value={form.capacity} onChange={set('capacity')} />
              <Field label="Gece sayısı" type="number" value={form.nights} onChange={set('nights')} />
              <Field label="Tek kişilik oda farkı (gece başı ₺)" type="number" value={form.singleRoomSupplement} onChange={set('singleRoomSupplement')} hint="0 yazarsanız not gösterilmez" />
              <Field label="Fiyat (sayı)" type="number" value={form.price} onChange={set('price')} />
              <Field label="Fiyat etiketi" value={form.priceLabel} onChange={set('priceLabel')} hint="Örn: 35.000₺" />
              <Field label="Şehir" value={form.locationCity} onChange={set('locationCity')} />
              <Field label="Bölge / il" value={form.locationRegion} onChange={set('locationRegion')} />
            </div>
            <Area label="Rota açıklaması" value={form.route} onChange={set('route')} rows={2} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-gray-100">
              <div className="space-y-2">
                <Field label="Hero görseli (URL)" value={form.heroImage} onChange={set('heroImage')} />
                <ImageUploader category="tours" label="Hero görseli yükle" onUpload={(url) => set('heroImage')(url)} />
              </div>
              <div className="space-y-2">
                <Field label="Yedek hero görseli (URL)" value={form.heroImageFallback} onChange={set('heroImageFallback')} hint="Ana görsel yüklenemezse bu kullanılır" />
                <Field label="Hero görsel alt metni" value={form.heroImageAlt} onChange={set('heroImageAlt')} />
              </div>
            </div>
            {form.heroImage && (
              <img src={form.heroImage} alt="" className="w-full max-w-sm h-32 object-cover rounded-lg border border-gray-200" />
            )}
            <div className="pt-2 border-t border-gray-100">
              <Toggle label="Yayında (site ve ana sayfada görünür)" checked={form.active} onChange={set('active')} testId="admin-event-active" />
            </div>
          </>
        )}

        {tab === 'hero' && (
          <>
            <Field label="Üst etiket" value={form.hero?.eyebrow} onChange={setNested('hero', 'eyebrow')} />
            <Area label="Başlık" value={form.hero?.title} onChange={setNested('hero', 'title')} rows={2} testId="admin-event-hero-title" />
            <Area label="Alt metin" value={form.hero?.subtitle} onChange={setNested('hero', 'subtitle')} rows={4} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Birincil buton" value={form.hero?.primaryCta} onChange={setNested('hero', 'primaryCta')} />
              <Field label="İkincil buton" value={form.hero?.secondaryCta} onChange={setNested('hero', 'secondaryCta')} />
            </div>
          </>
        )}

        {tab === 'rakamlar' && (
          <Repeater
            label="Rakam kartları"
            items={form.highlights}
            onChange={set('highlights')}
            blank={{ value: '', unit: '', label: '' }}
            titleOf={(it, i) => `Kart ${i + 1}`}
            testId="admin-event-highlights"
            render={(it, patch) => (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Değer" value={it.value} onChange={(v) => patch({ value: v })} />
                <Field label="Birim" value={it.unit} onChange={(v) => patch({ unit: v })} />
                <Field label="Açıklama" value={it.label} onChange={(v) => patch({ label: v })} />
              </div>
            )}
          />
        )}

        {tab === 'sohbetler' && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Üst etiket" value={form.talks?.eyebrow} onChange={setNested('talks', 'eyebrow')} hint="Örn: Akşam Sohbetleri" />
              <Field label="Bölüm başlığı" value={form.talks?.title} onChange={setNested('talks', 'title')} testId="admin-event-talks-title" />
            </div>
            <Area label="Bölüm alt metni" value={form.talks?.subtitle} onChange={setNested('talks', 'subtitle')} rows={3} />
            <StringList
              label="Konuşulacak konular"
              items={form.talks?.topics || []}
              onChange={setNested('talks', 'topics')}
              placeholder="Konu başlığı"
              testId="admin-event-topics"
            />
          </>
        )}

        {tab === 'program' && (
          <Repeater
            label="Gün gün program"
            items={form.itinerary}
            onChange={set('itinerary')}
            blank={{ day: 0, date: '', title: '', activities: [], km: 0, difficulty: 'Orta', stay: '', meals: '', eveningTalk: true }}
            titleOf={(it, i) => `Gün ${it.day || i + 1}`}
            testId="admin-event-days"
            render={(it, patch) => (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Field label="Gün no" type="number" value={it.day} onChange={(v) => patch({ day: v })} />
                  <Field label="Tarih" value={it.date} onChange={(v) => patch({ date: v })} hint="Örn: 7 Kasım Cumartesi" />
                  <Field label="Yürüyüş (km)" type="number" value={it.km} onChange={(v) => patch({ km: v })} />
                </div>
                <Field label="Gün başlığı" value={it.title} onChange={(v) => patch({ title: v })} />
                <StringList label="Aktiviteler" items={it.activities || []} onChange={(v) => patch({ activities: v })} testId="admin-event-day-activities" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Field label="Zorluk" value={it.difficulty} onChange={(v) => patch({ difficulty: v })} />
                  <Field label="Konaklama" value={it.stay} onChange={(v) => patch({ stay: v })} />
                  <Field label="Öğünler" value={it.meals} onChange={(v) => patch({ meals: v })} />
                </div>
                <Toggle label="Bu akşam sohbet var" checked={it.eveningTalk} onChange={(v) => patch({ eveningTalk: v })} />
              </div>
            )}
          />
        )}

        {tab === 'dahil' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <StringList label="Dahil olanlar" items={form.included} onChange={set('included')} testId="admin-event-included" />
            <StringList label="Dahil olmayanlar" items={form.excluded} onChange={set('excluded')} testId="admin-event-excluded" />
          </div>
        )}

        {tab === 'konuklar' && (
          <Repeater
            label="Konuklar"
            items={form.guests}
            onChange={set('guests')}
            blank={{ name: '', tag: '', photo: '', avatar: '', link: '' }}
            titleOf={(it, i) => it.name || `Konuk ${i + 1}`}
            testId="admin-event-guests"
            render={(it, patch) => (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Field label="Ad soyad" value={it.name} onChange={(v) => patch({ name: v })} />
                  <Field label="Etiket" value={it.tag} onChange={(v) => patch({ tag: v })} hint="Örn: 49W" />
                  <Field label="Link" value={it.link} onChange={(v) => patch({ link: v })} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Field label="Kart fotoğrafı (URL)" value={it.photo} onChange={(v) => patch({ photo: v })} />
                    <ImageUploader category="tours" label="Kart fotoğrafı yükle" onUpload={(url) => patch({ photo: url })} />
                  </div>
                  <div className="space-y-2">
                    <Field label="Hero avatarı (URL)" value={it.avatar} onChange={(v) => patch({ avatar: v })} />
                    <ImageUploader category="tours" label="Avatar yükle" onUpload={(url) => patch({ avatar: url })} />
                  </div>
                </div>
              </div>
            )}
          />
        )}

        {tab === 'partnerler' && (
          <Repeater
            label="Partnerler"
            items={form.partners}
            onChange={set('partners')}
            blank={{ key: '', name: '', role: '', description: '', logo: '', logoOnDark: '', link: '' }}
            titleOf={(it, i) => it.name || `Partner ${i + 1}`}
            testId="admin-event-partners"
            render={(it, patch) => (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Field label="Anahtar" value={it.key} onChange={(v) => patch({ key: v })} hint="Örn: 49w" />
                  <Field label="Ad" value={it.name} onChange={(v) => patch({ name: v })} />
                  <Field label="Rol" value={it.role} onChange={(v) => patch({ role: v })} />
                </div>
                <Area label="Tanım" value={it.description} onChange={(v) => patch({ description: v })} rows={2} />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Field label="Logo (açık zemin)" value={it.logo} onChange={(v) => patch({ logo: v })} />
                    <ImageUploader category="tours" label="Logo yükle" onUpload={(url) => patch({ logo: url })} />
                  </div>
                  <div className="space-y-2">
                    <Field label="Logo (koyu zemin)" value={it.logoOnDark} onChange={(v) => patch({ logoOnDark: v })} />
                    <ImageUploader category="tours" label="Koyu zemin logosu yükle" onUpload={(url) => patch({ logoOnDark: url })} />
                  </div>
                  <Field label="Link" value={it.link} onChange={(v) => patch({ link: v })} />
                </div>
              </div>
            )}
          />
        )}

        {tab === 'seo' && (
          <>
            <Field label="Sayfa başlığı (title)" value={form.seo?.title} onChange={setNested('seo', 'title')} hint="60 karakteri aşmamaya çalışın" />
            <Area label="Meta açıklama" value={form.seo?.description} onChange={setNested('seo', 'description')} rows={3} hint="155 karakter" />
            <div className="pt-4 border-t border-gray-100 space-y-4">
              <p className="text-sm font-bold text-[#1B1B1B]">Ana sayfa bölümü</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Rozet" value={form.homePromo?.badge} onChange={setNested('homePromo', 'badge')} />
                <Field label="Buton metni" value={form.homePromo?.cta} onChange={setNested('homePromo', 'cta')} />
              </div>
              <Area label="Başlık" value={form.homePromo?.title} onChange={setNested('homePromo', 'title')} rows={2} />
              <Area label="Alt metin" value={form.homePromo?.subtitle} onChange={setNested('homePromo', 'subtitle')} rows={3} />
            </div>
          </>
        )}
      </div>
    </form>
  );
};

export default AdminEventForm;
